import React from 'react';
import _ from 'underscore';

import {Source} from 'constants/SourceConstants';
import {IntentLevel} from 'file-browser/FileSelectorConstants';
import FolderSelector, {UNKNOWN_FOLDER} from 'file-browser/FolderSelector';
import preferences from 'Preferences';
import {ButtonSize} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

interface BaseSourceSelectorProps {
  areSourcesLoading?: boolean;
  buttonSize?: ButtonSize;
  selectedSourceId: string | null;
  showRegistries: boolean;
  sources: Array<Source>;
  readOnly?: boolean;
  level?: IntentLevel;
  enableRedesign: boolean;
  /** Key where last selected source ID will be saved to. */
  preferenceKey?: string;
  /**
   * If the selector modal is opened from a tooltip, its z-index may need to be raised to render
   * on top of that tooltip.
   *
   * @default 1040
   */
  selectorModalZIndex?: 1040 | 1060;
}

export type NonNullableSourceSelectorProps = BaseSourceSelectorProps & {
  allowNone?: false;
  onSourceSelected: (sourceId: string) => void;
};

export type NullableSourceSelectorProps = BaseSourceSelectorProps & {
  allowNone: true;
  onSourceSelected: (sourceId: string | null) => void;
};

export type SourceSelectorProps = NonNullableSourceSelectorProps | NullableSourceSelectorProps;

export function SourceSelector(props: SourceSelectorProps): JSX.Element {
  if (props.areSourcesLoading) {
    return <CenteredHorizontalThrobber />;
  }

  let rootFolderId: string | null;
  if (props.selectedSourceId) {
    const selectedSource = _.findWhere(props.sources, {id: props.selectedSourceId});
    // selectedSource is null if it could not be found, e.g. if the user does not have access
    rootFolderId = selectedSource ? selectedSource.rootFolder.api_identifier : UNKNOWN_FOLDER;
  } else {
    rootFolderId = null;
  }

  function handleChangeSource(e: {target: {value: string}}): void {
    const source = props.sources.find((s) => s.rootFolder.api_identifier === e.target.value);
    if (props.allowNone && !source?.id && props.selectedSourceId != null) {
      props.onSourceSelected(null);
    } else if (source && props.selectedSourceId !== source.id) {
      props.onSourceSelected(source.id);
    }
    if (props.preferenceKey != null) {
      preferences.set(props.preferenceKey, source?.id ?? null);
    }
  }

  return (
    <FolderSelector
      buttonSize={props.buttonSize}
      noFolderText={props.showRegistries ? 'Choose project or registry' : 'Choose project'}
      value={rootFolderId}
      onChange={handleChangeSource}
      // The combination of showInventory=showNotebook=false is what disables sub-folder selection
      showInventory={false}
      showNotebook={false}
      showRegistries={props.showRegistries}
      readOnly={props.readOnly}
      level={props.level}
      enableRedesign={props.enableRedesign}
      allowNone={props.allowNone}
      selectorModalZIndex={props.selectorModalZIndex}
      unsetOnFetchFail={true}
    />
  );
}

SourceSelector.defaultProps = {
  readOnly: false,
  enableRedesign: false,
  allowNone: false,
};

export default SourceSelector;
